import type { ReadonlyURLSearchParams } from "next/navigation";
import type { GetPageResponse } from "@store/features/crm/pageV2Slice";
import type { StructuredExpression } from "../helpers/urlFilters";
import {
  isEmptyStructuredExpression,
  searchParamsToSort,
  searchParamsToStructuredFilters,
} from "../helpers/urlFilters";
import type { StructuredFilter } from "skedyul-core/features/crm/resources/filterTypes";
import type { QuerySort } from "skedyul-ui/ui";
import {
  mergePageFilters,
  mergePageSort,
} from "skedyul-core/features/crm/features/pages/utils/mergePageQuery";
import type { Query } from "skedyul-core/features/crm/features/pages/features/queries/resources/types";

type BaseQueryFilter = NonNullable<Query["filters"]>[number];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const structuredOpToLegacy = (
  op: string,
): BaseQueryFilter["operator"] => {
  if (op === "neq") return "ne";
  if (
    op === "eq" ||
    op === "ne" ||
    op === "gt" ||
    op === "gte" ||
    op === "lt" ||
    op === "lte"
  ) {
    return op;
  }
  return "eq";
};

const stringifyFilterValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(",");
  }
  return String(value);
};

const isStructuredFilter = (value: unknown): value is StructuredFilter =>
  isPlainObject(value) &&
  !("AND" in value) &&
  !("OR" in value) &&
  !("NOT" in value);

const collectLeafFilters = (
  expr: StructuredExpression | undefined,
): StructuredFilter[] => {
  if (!expr || isEmptyStructuredExpression(expr)) {
    return [];
  }

  if (isStructuredFilter(expr)) {
    return [expr];
  }

  if (isPlainObject(expr) && Array.isArray(expr.AND)) {
    return (expr.AND as StructuredExpression[]).flatMap(collectLeafFilters);
  }

  return [];
};

const structuredFilterToBaseQueryFilters = (
  filter: StructuredFilter,
): BaseQueryFilter[] => {
  const results: BaseQueryFilter[] = [];

  for (const fieldKey of Object.keys(filter)) {
    if (fieldKey === "search") {
      continue;
    }

    const conditions = filter[fieldKey];
    if (!conditions || typeof conditions !== "object") {
      continue;
    }

    for (const operator of Object.keys(conditions)) {
      const value = conditions[operator as keyof typeof conditions];
      if (value === undefined) {
        continue;
      }

      results.push({
        field: fieldKey,
        operator: structuredOpToLegacy(operator),
        value: stringifyFilterValue(value),
      });
    }
  }

  return results;
};

const mergedFiltersToBaseQueryFilters = (
  merged: StructuredExpression | undefined,
): BaseQueryFilter[] => {
  return collectLeafFilters(merged).flatMap(structuredFilterToBaseQueryFilters);
};

const mergedSortToBaseQuerySort = (
  sort: QuerySort | undefined,
): Query["sort"] | undefined => {
  if (!sort) {
    return undefined;
  }

  const resolved = Array.isArray(sort) ? sort[0] : sort;
  if (!resolved?.key) {
    return undefined;
  }

  return {
    field: resolved.key,
    direction: resolved.direction === "asc" ? "asc" : "desc",
  };
};

type LayoutDraftLike = {
  draftBaseQuery?: GetPageResponse["baseQuery"];
  originalBaseQuery?: GetPageResponse["baseQuery"];
} | null;

export const resolveEffectivePageBaseQuery = (
  pageData: GetPageResponse | null | undefined,
  layoutDraft: LayoutDraftLike,
): GetPageResponse["baseQuery"] => {
  if (layoutDraft?.draftBaseQuery !== undefined) {
    return layoutDraft.draftBaseQuery ?? null;
  }

  return pageData?.baseQuery ?? layoutDraft?.originalBaseQuery ?? null;
};

export const readEphemeralQueryFromLocation = (): {
  filters: StructuredExpression;
  sort: QuerySort | undefined;
} => {
  if (typeof window === "undefined") {
    return { filters: {}, sort: undefined };
  }

  const params = new URLSearchParams(window.location.search);
  const readOnlyParams = params as unknown as ReadonlyURLSearchParams;
  return {
    filters: searchParamsToStructuredFilters(readOnlyParams),
    sort: searchParamsToSort(readOnlyParams),
  };
};

export const mergeEphemeralQueryWithPageBaseQuery = (
  baseQuery: GetPageResponse["baseQuery"],
  ephemeralFilters?: StructuredExpression,
  ephemeralSort?: QuerySort,
): {
  filters?: StructuredExpression;
  sort?: ReturnType<typeof mergePageSort>;
} => {
  const mergedFilters = mergePageFilters(
    (baseQuery ?? null) as Query | null,
    ephemeralFilters && !isEmptyStructuredExpression(ephemeralFilters)
      ? ephemeralFilters
      : undefined,
  );
  const mergedSort = mergePageSort(
    (baseQuery ?? null) as Query | null,
    ephemeralSort,
  );

  return {
    filters:
      mergedFilters &&
      !isEmptyStructuredExpression(mergedFilters as StructuredExpression)
        ? (mergedFilters as StructuredExpression)
        : undefined,
    sort: mergedSort,
  };
};

export const mergeBaseQueryForSave = (
  baseQuery: GetPageResponse["baseQuery"],
  ephemeralFilters: StructuredExpression,
  ephemeralSort: QuerySort | undefined,
): GetPageResponse["baseQuery"] => {
  const mergedFiltersExpr = mergePageFilters(
    (baseQuery ?? null) as Query | null,
    isEmptyStructuredExpression(ephemeralFilters) ? undefined : ephemeralFilters,
  );

  const mergedSort = mergePageSort(
    (baseQuery ?? null) as Query | null,
    ephemeralSort,
  );

  const filters = mergedFiltersToBaseQueryFilters(
    mergedFiltersExpr as StructuredExpression | undefined,
  );
  const sort = mergedSortToBaseQuerySort(mergedSort as QuerySort | undefined);

  if (filters.length === 0 && !sort) {
    return {
      filters: [],
      sort: undefined,
    };
  }

  return {
    filters,
    ...(sort ? { sort } : {}),
  };
};
